/**
 * LearningCatalog — freeze dry academy learning board (v2).
 *
 * Layout inspired by the superLMS reference the user shared, adapted to the
 * HomeLyph Pro navy + amber theme:
 *
 *   ┌──────────── ToolSelector ────────────┐
 *   │ Home >  Learning                     │
 *   ├─────────┬──────────────────────┬─────┤
 *   │  Sub-   │  Summary of Learning │ Pro │
 *   │  nav    │  Current Courses     │ mo  │
 *   │         │  Special Events      │ +   │
 *   │         │                      │ Rec │
 *   └─────────┴──────────────────────┴─────┘
 *
 * Sub-nav items on the left (Home, Enrolled, Certifications, Roadmap,
 * Special Events, Bookmarks) filter the main grid. The right rail promotes
 * Pro membership + surfaces the graduate's Recent lessons feed.
 */
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import AdSlot from '../components/AdSlot';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  BookOpen, Search, Clock, Award, ArrowUpRight, ArrowRight,
  Home as HomeIcon, GraduationCap, MapPinned, Sparkles, Bookmark,
  BookmarkPlus, BookmarkCheck, PlayCircle, CheckCircle2, ListChecks,
  Lock, TrendingUp, CalendarDays, Loader2, Bell, Archive, ArchiveRestore,
  XCircle, Users, Video, Presentation,
} from 'lucide-react';
import ToolSelector from '../components/ToolSelector';
import LearningTour from '../components/LearningTour';
import { TourLaunchButton } from '../components/PageTour';
import FreedrIQSplash from '../components/freedriq/FreedrIQSplash';
import ChallengeBanner from '../components/freedriq/ChallengeBanner';
import UserEngagementBanner from '../components/UserEngagementBanner';
import { CompletedPill } from '../components/lms/CompletionBadges';
import { stripAndTruncate } from '../lib/richText';

// -------------------------------------------------------------------------
// Sub-nav definitions
// -------------------------------------------------------------------------
const SUBNAV = [
  { id: 'home',        label: 'Home',            icon: HomeIcon },
  { id: 'courses',     label: 'Courses',         icon: BookOpen },
  { id: 'enrolled',    label: 'Your courses',    icon: PlayCircle },
  { id: 'certs',       label: 'Certifications',  icon: Award },
  { id: 'roadmap',     label: 'Roadmap',         icon: MapPinned },
  { id: 'special',     label: 'Special Events',  icon: Sparkles },
  { id: 'studygroups', label: 'Study Groups',    icon: Users },
  { id: 'bookmarks',   label: 'Bookmarks',       icon: Bookmark },
  { id: 'graduates',   label: 'Graduate Wall',   icon: GraduationCap },
];

const TIER_FILTERS = [
  { id: 'all',        label: 'All' },
  { id: 'free',       label: 'Free' },
  { id: 'pro',        label: 'Pro' },
  { id: 'business',   label: 'Business' },
  { id: 'enterprise', label: 'Enterprise' },
  { id: 'specialty',  label: 'Specialty' },
  { id: 'paid',       label: 'Certificate' },
];

const STATUS_FILTERS = [
  { id: 'any',      label: 'All statuses' },
  { id: 'active',   label: 'Active only' },
  { id: 'archived', label: 'Archived only' },
];

// Progressive upgrade path — each tier prompts for the *next* level.
const UPGRADE_PATH = {
  free:       { next: 'Pro',        blurb: 'Unlock all Pro courses, live labs, and signed certificates.', accent: 'bg-amber-500 hover:bg-amber-600 text-navy-950' },
  pro:        { next: 'Business',   blurb: 'Add team seats, batch-tracking dashboards, and revenue analytics.', accent: 'bg-primary hover:bg-primary/90 text-primary-foreground' },
  business:   { next: 'Enterprise', blurb: 'Custom SLAs, white-label branding, and a dedicated success partner.', accent: 'bg-primary hover:bg-primary/90 text-primary-foreground' },
  enterprise: null,  // top tier — nothing to upgrade to
};

const TIER_META = {
  free:       { label: 'Free',        badge: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  pro:        { label: 'Pro',         badge: 'bg-amber-500/15 text-amber-700 border-amber-500/30' },
  business:   { label: 'Business',    badge: 'bg-primary/15 text-primary border-primary/30' },
  enterprise: { label: 'Enterprise',  badge: 'bg-violet-500/15 text-violet-700 border-violet-500/30' },
  specialty:  { label: 'Specialty',   badge: 'bg-rose-500/15 text-rose-700 border-rose-500/30' },
  paid:       { label: 'Certificate', badge: 'bg-primary/15 text-primary border-primary/30' },
};

// Fallback themes shown when the loaded courses don't yet carry `category`
// or `skill_tags`. Keeps the Theme chip strip visible so users know the
// feature exists — clicking a chip still filters against tier / title.
// Curated theme vocabulary — must mirror backend `COURSE_THEMES` in
// /app/backend/lms_models.py. Frontend uses this as the fallback (when no
// courses are tagged yet) AND as a title-cased pretty label lookup.
const CURATED_THEMES = ['beginner', 'business', 'equipment', 'recipes', 'nutrition', 'safety'];
const FALLBACK_THEMES = CURATED_THEMES.map((t) => t.charAt(0).toUpperCase() + t.slice(1));

// -------------------------------------------------------------------------
// Small building blocks
// -------------------------------------------------------------------------
const StatChip = ({ icon: Icon, label, value, accent = 'text-primary' }) => (
  <div className="flex items-center gap-2">
    <div className={`h-8 w-8 rounded-lg bg-muted flex items-center justify-center ${accent}`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="leading-tight">
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
      <div className="text-sm font-extrabold text-foreground">{value}</div>
    </div>
  </div>
);

const CourseCard = ({ course, onBookmark, onOpen, onEnroll, onReminder, onArchive, onDrop, isProUser, hasReminder, prereqLock }) => {
  const tierMeta = TIER_META[course.tier] || TIER_META.free;
  const pct = course.progress_percentage || 0;
  const isCompleted = pct === 100;
  const inProgress = pct > 0 && pct < 100;
  const isEnrolled = course.is_enrolled || pct > 0;
  const isArchived = !!course.is_archived;
  const gated = (course.tier === 'pro' && !isProUser) || course.tier === 'paid';
  // Prerequisite lock — set by parent when the tier's learning path
  // is saved with enforce_prerequisites and a previous step isn't yet
  // completed. Soft UI lock: badge + tooltip + disabled Open CTA.
  const locked = !!prereqLock;
  return (
    <div
      className={`pro-card p-0 overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg ${isArchived ? 'opacity-80 hover:border-muted-foreground/40' : 'hover:border-primary/60'}`}
      data-testid={`course-card-${course.id}`}
    >
      {/* Header strip */}
      <div className="bg-navy-spotlight text-background p-4 relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-primary/20 blur-2xl" />
        <div className="relative flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${tierMeta.badge} bg-background/95`}>
              {tierMeta.label}
            </span>
            {locked && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-amber-500/95 text-white border border-amber-600"
                title={prereqLock?.reason || 'Complete the previous course in this tier first'}
                data-testid={`course-locked-pill-${course.id}`}
              >
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
            {isCompleted && (
              <CompletedPill testid={`course-completed-pill-${course.id}`} />
            )}
            {isArchived && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-background/15 border border-background/30 text-background/85">
                <Archive className="w-3 h-3" /> Archived
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {isEnrolled && onDrop && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDrop(course); }}
                className="h-7 w-7 rounded-full bg-background/10 hover:bg-rose-500/30 flex items-center justify-center text-background/80 hover:text-rose-200 transition-colors"
                aria-label="Drop this course from your list"
                title="Drop course"
                data-testid={`course-drop-${course.id}`}
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            )}
            {isEnrolled && onArchive && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onArchive(course); }}
                className="h-7 w-7 rounded-full bg-background/10 hover:bg-background/25 flex items-center justify-center text-background/80 hover:text-background transition-colors"
                aria-label={isArchived ? 'Restore course to active' : 'Archive course'}
                title={isArchived ? 'Restore to active' : 'Archive course'}
                data-testid={`course-archive-${course.id}`}
              >
                {isArchived
                  ? <ArchiveRestore className="w-3.5 h-3.5 text-emerald-300" />
                  : <Archive className="w-3.5 h-3.5" />}
              </button>
            )}
            {isEnrolled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onReminder(course); }}
                className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${hasReminder ? 'bg-amber-500/25 text-amber-300' : 'bg-background/10 hover:bg-background/25 text-background/80'}`}
                aria-label={hasReminder ? 'Edit reminder' : 'Set reminder'}
                data-testid={`course-reminder-${course.id}`}
              >
                <Bell className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onBookmark(course); }}
              className="h-7 w-7 rounded-full bg-background/10 hover:bg-background/25 flex items-center justify-center text-background/80 hover:text-background transition-colors"
              aria-label={course.is_bookmarked ? 'Remove bookmark' : 'Bookmark course'}
              data-testid={`course-bookmark-${course.id}`}
            >
              {course.is_bookmarked
                ? <BookmarkCheck className="w-3.5 h-3.5 text-amber-400" />
                : <BookmarkPlus className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <h3 className="mt-3 font-heading text-lg font-extrabold tracking-tight leading-snug line-clamp-2">
          {course.title}
        </h3>
        {course.description && (
          <p className="mt-1 text-[12px] text-background/70 line-clamp-2">{stripAndTruncate(course.description, 160)}</p>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 text-[11px] font-semibold text-muted-foreground">
          <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {course.modules_count || 0} modules</span>
          <span className="inline-flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> {course.total_lessons || 0} lessons</span>
          {course.duration_minutes ? (
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {Math.round(course.duration_minutes / 60)}h</span>
          ) : null}
        </div>

        {locked && (
          <div
            className="mt-3 rounded-md border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-[11px] space-y-1.5"
            data-testid={`course-locked-progress-${course.id}`}
          >
            <div className="flex items-start gap-1.5 text-amber-900 dark:text-amber-100">
              <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-bold">Locked — finish the previous course first.</div>
                <div className="text-amber-800 dark:text-amber-200/90 truncate">
                  {(() => {
                    const total = prereqLock.prevLessonsTotal || 0;
                    const done  = prereqLock.prevLessonsDone  || 0;
                    const remaining = Math.max(0, total - done);
                    if (total > 0) {
                      return `You're ${prereqLock.prevPct || 0}% through “${prereqLock.blockedTitle}” — ${remaining} lesson${remaining === 1 ? '' : 's'} to go.`;
                    }
                    return `Complete “${prereqLock.blockedTitle}” to unlock this course.`;
                  })()}
                </div>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-amber-200/60 dark:bg-amber-900/40 overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: `${Math.max(2, prereqLock.prevPct || 0)}%` }} />
            </div>
            {prereqLock.blockedCourseId ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen({ id: prereqLock.blockedCourseId, title: prereqLock.blockedTitle });
                }}
                className="text-[11px] font-bold underline text-amber-900 dark:text-amber-100 hover:text-amber-700"
                data-testid={`course-goto-prev-${course.id}`}
              >
                Go to “{prereqLock.blockedTitle}” →
              </button>
            ) : null}
          </div>
        )}

        {isEnrolled && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] font-semibold">
              <span className="text-muted-foreground">
                {isCompleted ? 'Complete' : inProgress ? `${course.completed_lessons} / ${course.total_lessons} lessons` : 'Not started'}
              </span>
              <span className={isCompleted ? 'text-emerald-600' : 'text-foreground/70'}>{pct}%</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all ${isCompleted ? 'bg-emerald-500' : 'bg-primary'}`}
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          {isEnrolled ? (
            <Button
              size="sm"
              onClick={() => onOpen(course)}
              className={`rounded-full font-bold ${isCompleted ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-primary hover:bg-primary/90'}`}
              data-testid={`course-open-${course.id}`}
            >
              {isCompleted ? (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Review</>)
                : inProgress ? (<><PlayCircle className="w-3.5 h-3.5 mr-1" /> Continue</>)
                : (<>Start course <ArrowRight className="w-3.5 h-3.5 ml-1" /></>)}
            </Button>
          ) : gated ? (
            <Button
              size="sm"
              onClick={() => onEnroll(course)}
              className={`rounded-full font-bold ${course.tier === 'paid' ? 'bg-primary hover:bg-primary/90' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
              data-testid={`course-gated-${course.id}`}
            >
              {course.tier === 'paid' ? (<><Award className="w-3.5 h-3.5 mr-1" /> Purchase seat</>)
                : (<><Lock className="w-3.5 h-3.5 mr-1" /> Upgrade to enroll</>)}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => onEnroll(course)}
              className="rounded-full font-bold bg-primary hover:bg-primary/90"
              data-testid={`course-enroll-${course.id}`}
            >
              <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Add to my courses
            </Button>
          )}
          {course.tier === 'pro' && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 flex items-center gap-1">
              <Award className="w-3 h-3" /> Pro
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------------
const LearningCatalog = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { API, user } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeNav, setActiveNav] = useState('home');
  const [tierFilter, setTierFilter] = useState('all');
  const [recentProgress, setRecentProgress] = useState([]);
  const [events, setEvents] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [graduates, setGraduates] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [reminderCourse, setReminderCourse] = useState(null);
  const [streak, setStreak] = useState({ current: 0, longest: 0, days_active: 0 });
  const [plannerOpen, setPlannerOpen] = useState(false);
  // Struggling-user handoff — /lms/confidence pushes users here with an
  // `?ai_help=...` payload so the planner opens pre-loaded with their
  // context.
  const [plannerInitialMessage, setPlannerInitialMessage] = useState('');
  const [tourOpen, setTourOpen] = useState(false);
  const [activeThemes, setActiveThemes] = useState([]);

  // Auto-open the AI Teacher planner when the confidence-survey CTA routes
  // the user in with a pre-filled prompt. Strips the param after consuming
  // so refreshing doesn't reopen the dialog.
  useEffect(() => {
    const raw = searchParams.get('ai_help');
    if (raw) {
      setPlannerInitialMessage(raw);
      setPlannerOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('ai_help');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  // Compute the set of themes present in the loaded courses so the chip
  // strip only shows real, populated options (no dead themes). Falls back
  // to a curated preset when the seed data hasn't tagged its courses yet
  // so the filter surface is always visible.
  const availableThemes = useMemo(() => {
    const seen = new Map();
    for (const c of courses) {
      // Curated `themes` array first (from the fixed CURATED_THEMES list),
      // then legacy `category` + free-form `skill_tags`.
      const raw = [...(c.themes || []), c.category, ...(c.skill_tags || [])].filter(Boolean);
      for (const r of raw) {
        const key = String(r).trim();
        if (!key) continue;
        // Title-case the display label but key by lowercase for dedupe.
        const pretty = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
        seen.set(key.toLowerCase(), pretty);
      }
    }
    if (seen.size > 0) return Array.from(seen.values()).sort();
    return FALLBACK_THEMES.slice();
  }, [courses]);
  const [statusFilter, setStatusFilter] = useState('active'); // 'any' | 'active' | 'archived'

  const currentTier = (user?.role || 'free').toLowerCase();
  const isProUser = ['pro', 'business', 'enterprise', 'admin'].includes(currentTier);
  const upgradeInfo = UPGRADE_PATH[currentTier] || UPGRADE_PATH.free;

  // Learning paths per tier (saved by admins via the Curriculum Map).
  // Shape: { [tier]: { steps: [{course_id, order, title}], enforce_prerequisites } }
  const [learningPaths, setLearningPaths] = useState({});
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      ['free', 'pro', 'business', 'enterprise'].map((t) =>
        axios.get(`${API}/lms/learning-path/${t}`)
          .then((r) => [t, r.data])
          .catch(() => [t, null]),
      ),
    ).then((entries) => {
      if (cancelled) return;
      const map = {};
      for (const [t, d] of entries) if (d) map[t] = d;
      setLearningPaths(map);
    });
    return () => { cancelled = true; };
  }, [API]);

  // Build a per-course prereq-lock map. A course is locked iff:
  //   (a) its tier has a saved path with enforce_prerequisites=true
  //   (b) its position in that path is > 1
  //   (c) at least one preceding step isn't fully completed by the user
  //
  // The lock descriptor also carries the previous course's title +
  // progress so the card can render an actionable callout:
  //   "You're 40% through Welcome to Freeze Drying — 3 lessons to go."
  const prereqLockMap = useMemo(() => {
    const out = {};
    for (const tier of Object.keys(learningPaths)) {
      const lp = learningPaths[tier];
      if (!lp?.enforce_prerequisites || !Array.isArray(lp.steps)) continue;
      for (let i = 0; i < lp.steps.length; i++) {
        const step = lp.steps[i];
        if (!step?.course_id) continue;
        if (i === 0) continue; // first step never locked
        // Find any preceding step that isn't completed by the current user.
        for (let j = 0; j < i; j++) {
          const prevStep = lp.steps[j];
          const prevId = prevStep?.course_id;
          if (!prevId) continue;
          const prev = courses.find((c) => c.id === prevId);
          const pct = prev ? (prev.progress_percentage || 0) : 0;
          const done = pct === 100;
          if (!done) {
            out[step.course_id] = {
              reason: `Complete "${prevStep?.title || 'the previous course'}" first to unlock this one.`,
              tier,
              stepOrder: i + 1,
              blockedTitle: prevStep?.title || prev?.title || 'the previous course',
              blockedCourseId: prevId,
              prevPct: pct,
              prevLessonsTotal: prev?.total_lessons || 0,
              prevLessonsDone:  prev?.completed_lessons || 0,
            };
            break;
          }
        }
      }
    }
    return out;
  }, [learningPaths, courses]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/lms/courses`)
      .then((res) => { if (!cancelled) setCourses(res.data.courses || []); })
      .catch(() => { if (!cancelled) setCourses([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    axios.get(`${API}/lms/my-progress`)
      .then((res) => { if (!cancelled) setRecentProgress(res.data?.progress || res.data?.items || []); })
      .catch(() => { /* noop */ });
    axios.get(`${API}/public/events?limit=8`)
      .then((res) => { if (!cancelled) setEvents(res.data?.items || []); })
      .catch(() => { /* noop */ });
    axios.get(`${API}/lms/reminders`)
      .then((res) => { if (!cancelled) setReminders(res.data?.items || []); })
      .catch(() => { /* noop */ });
    axios.get(`${API}/graduates?limit=6`)
      .then((res) => { if (!cancelled) setGraduates(res.data?.graduates || []); })
      .catch(() => { /* noop */ });
    axios.get(`${API}/lms/my-certificates`)
      .then((res) => { if (!cancelled) setCertificates(res.data?.certificates || []); })
      .catch(() => { /* noop */ });
    axios.get(`${API}/lms/streak`)
      .then((res) => { if (!cancelled) setStreak(res.data || streak); })
      .catch(() => { /* noop */ });
    return () => { cancelled = true; };
  }, [API]);

  // When a lesson gets completed inside the LessonViewer, refetch the
  // progress + courses lists so the catalog's percentage bars and
  // completion pills reflect the change without a manual reload.
  useEffect(() => {
    const handler = () => {
      axios.get(`${API}/lms/courses`)
        .then((res) => setCourses(res.data.courses || []))
        .catch(() => {});
      axios.get(`${API}/lms/my-progress`)
        .then((res) => setRecentProgress(res.data?.progress || res.data?.items || []))
        .catch(() => {});
    };
    window.addEventListener('lms:progress-updated', handler);
    return () => window.removeEventListener('lms:progress-updated', handler);
  }, [API]);

  // ---------------------------------------------------------------------
  // Derived summaries
  // ---------------------------------------------------------------------
  const summary = useMemo(() => {
    const started = courses.filter((c) => (c.completed_lessons || 0) > 0);
    const completed = started.filter((c) => (c.progress_percentage || 0) === 100);
    const inProgress = started.filter((c) => (c.progress_percentage || 0) < 100);
    const upcoming = courses.filter((c) => (c.completed_lessons || 0) === 0 && c.is_bookmarked);
    const totalLessonsCompleted = started.reduce((s, c) => s + (c.completed_lessons || 0), 0);
    const totalLessons = courses.reduce((s, c) => s + (c.total_lessons || 0), 0);
    const avgProgress = started.length
      ? Math.round(started.reduce((s, c) => s + (c.progress_percentage || 0), 0) / started.length)
      : 0;
    return {
      startedCount: started.length,
      completedCount: completed.length,
      inProgressCount: inProgress.length,
      upcomingCount: upcoming.length,
      totalLessonsCompleted,
      totalLessons,
      avgProgress,
    };
  }, [courses]);

  const filteredCourses = useMemo(() => {
    let list = courses;
    if (activeNav === 'enrolled') {
      list = list.filter((c) => (c.completed_lessons || 0) > 0 || c.is_enrolled);
    }
    else if (activeNav === 'bookmarks') list = list.filter((c) => c.is_bookmarked);
    else if (activeNav === 'special') list = [];

    // Status filter — applies wherever it makes sense (enrolled + home + courses).
    if (['home', 'courses', 'enrolled'].includes(activeNav)) {
      if (statusFilter === 'active')   list = list.filter((c) => !c.is_archived);
      if (statusFilter === 'archived') list = list.filter((c) => c.is_archived);
    }

    if (tierFilter !== 'all') list = list.filter((c) => (c.tier || 'free') === tierFilter);

    // Theme filter — matches against curated themes, category, skill_tags,
    // title, description so the chip catches even loosely-tagged courses.
    if (activeThemes.length) {
      const keys = activeThemes.map((t) => t.toLowerCase());
      list = list.filter((c) => {
        const hay = [
          ...(c.themes || []),
          c.category, ...(c.skill_tags || []),
          c.title, c.description,
        ].filter(Boolean).map((x) => String(x).toLowerCase()).join(' | ');
        return keys.some((k) => hay.includes(k));
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((c) =>
        (c.title || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [courses, activeNav, tierFilter, activeThemes, searchQuery, statusFilter]);

  const enrollCourse = async (course) => {
    try {
      const { data } = await axios.post(`${API}/lms/enroll`, { course_id: course.id });
      if (data?.requires_upgrade) { toast.info('Upgrade to Pro to enroll'); navigate('/pricing'); return; }
      if (data?.requires_purchase) { toast.info('Certificate courses require a purchase'); navigate('/pricing'); return; }
      toast.success('Added to your courses');
      setCourses((prev) => prev.map((c) => c.id === course.id ? { ...c, is_enrolled: true } : c));
    } catch (_) {
      toast.error('Could not enroll');
    }
  };

  const toggleArchive = async (course) => {
    const next = !course.is_archived;
    try {
      await axios.put(`${API}/lms/enrollments/${course.id}/archive`, { archived: next });
      setCourses((prev) => prev.map((c) => c.id === course.id ? { ...c, is_archived: next } : c));
      toast.success(next ? 'Course archived' : 'Course restored to Active');
    } catch (_) {
      toast.error('Could not update course');
    }
  };

  // Drop (unenroll) a course from the user's list. Progress is preserved so
  // they can re-enroll later without losing their work.
  const dropCourse = async (course) => {
    const ok = window.confirm(`Drop "${course.title}" from your list? Your lesson progress will be preserved.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/lms/enrollments/${course.id}`);
      setCourses((prev) => prev.map((c) => c.id === course.id
        ? { ...c, is_enrolled: false, is_archived: false, completed_lessons: 0, progress_percentage: 0 }
        : c));
      toast.success('Course dropped');
    } catch (_) {
      toast.error('Could not drop the course');
    }
  };

  const saveReminder = async (course, payload) => {
    try {
      const { data } = await axios.put(`${API}/lms/reminders/${course.id}`, { course_id: course.id, ...payload });
      setReminders((prev) => {
        const others = prev.filter((r) => r.course_id !== course.id);
        return [...others, data];
      });
      toast.success('Reminder saved');
      setReminderCourse(null);
    } catch (_) {
      toast.error('Could not save reminder');
    }
  };

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------
  const toggleBookmark = async (course) => {
    try {
      if (course.is_bookmarked) {
        await axios.delete(`${API}/lms/bookmarks/${course.id}`);
      } else {
        await axios.post(`${API}/lms/bookmarks`, { course_id: course.id });
      }
      setCourses((prev) => prev.map((c) => c.id === course.id ? { ...c, is_bookmarked: !c.is_bookmarked } : c));
      toast.success(course.is_bookmarked ? 'Bookmark removed' : 'Course bookmarked');
    } catch (_) {
      toast.error('Could not update bookmark');
    }
  };

  const openCourse = (course) => navigate(`/course/${course.id}`);

  const handleNavClick = (item) => {
    setActiveNav(item.id);
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-background" data-testid="learning-page">
      <ToolSelector />
      <LearningTour forceOpen={tourOpen} onFinish={() => setTourOpen(false)} />
      <FreedrIQSplash />

      <div className="container mx-auto px-4 sm:px-6 pt-6">
        <ChallengeBanner />
        <UserEngagementBanner />
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6">
        <div className="grid gap-6 lg:grid-cols-[220px,1fr,300px]">
          {/* -------- LEFT SUB-NAV ---------- */}
          <aside className="hidden lg:block" data-testid="learning-subnav">
            <div className="sticky top-20 space-y-1">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground px-3 py-2">
                Learning board
              </div>
              {SUBNAV.map((item) => {
                const active = activeNav === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavClick(item)}
                    aria-current={active ? 'page' : undefined}
                    data-testid={`subnav-${item.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold transition-colors
                      ${active
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/75 hover:bg-muted hover:text-foreground'}
                    `}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                  </button>
                );
              })}

              {/* Prof. FreedrIQ CTA — always visible */}
              <button
                type="button"
                onClick={() => setPlannerOpen(true)}
                className="mt-4 w-full rounded-2xl bg-primary/10 hover:bg-primary/15 border border-primary/30 p-3 text-left transition-colors"
                data-testid="ai-planner-open"
              >
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-extrabold text-foreground">Prof. FreedrIQ</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Recommend my path</div>
                  </div>
                </div>
              </button>

              {/* Progressive upgrade box — adapts to the user's current tier */}
              {upgradeInfo && (
                <div className="mt-4 rounded-2xl bg-navy-spotlight text-background p-4 relative overflow-hidden" data-testid="upgrade-box">
                  <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-amber-500/25 blur-2xl" />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300">
                      Level up · {currentTier[0].toUpperCase() + currentTier.slice(1)} → {upgradeInfo.next}
                    </div>
                    <div className="mt-1 font-heading text-sm font-extrabold leading-tight">
                      Upgrade to {upgradeInfo.next}
                    </div>
                    <p className="text-[11px] text-background/70 mt-1">
                      {upgradeInfo.blurb}
                    </p>
                    <Button
                      size="sm"
                      onClick={() => navigate('/pricing')}
                      className={`mt-3 w-full rounded-full font-bold ${upgradeInfo.accent}`}
                      data-testid="upgrade-box-cta"
                    >
                      Upgrade now <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* -------- MAIN COLUMN ---------- */}
          <main className="min-w-0 space-y-6">
            {/* Tutorial hint */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Link
                to="/tutorial/learning"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                data-testid="learning-tutorial-link"
              >
                <BookOpen className="w-3.5 h-3.5" /> New here? Take the Learning tutorial →
              </Link>
              <TourLaunchButton onClick={() => setTourOpen(true)} label="Take the on-page tour" testId="learning-tour-launch" />
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search courses, lessons, topics…"
                className="w-full pl-11 pr-4 h-11 rounded-full border border-border bg-card text-sm font-semibold placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
                data-testid="learning-search"
              />
            </div>

            {activeNav === 'home' && (
              <>
                {/* SUMMARY OF LEARNING PROCESS */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="eyebrow text-muted-foreground">Summary of learning process</h2>
                    <button
                      type="button"
                      onClick={() => setActiveNav('enrolled')}
                      className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1"
                      data-testid="summary-see-enrolled"
                    >
                      In-progress courses <ArrowUpRight className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {/* Enrolled */}
                    <div className="pro-card p-4" data-testid="summary-enrolled">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                          <PlayCircle className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-muted-foreground">
                            You are enrolled in
                          </div>
                          <div className="font-heading text-xl font-extrabold text-foreground leading-tight">
                            {summary.inProgressCount} course{summary.inProgressCount === 1 ? '' : 's'}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            Enhance your skills through active learning
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        <StatChip icon={CheckCircle2} label="Progress" value={`${summary.avgProgress}%`} accent="text-emerald-600" />
                        <StatChip icon={ListChecks} label="Lessons" value={`${summary.totalLessonsCompleted}/${summary.totalLessons}`} accent="text-amber-700" />
                        <StatChip icon={TrendingUp} label="Started" value={summary.startedCount} accent="text-primary" />
                      </div>
                    </div>

                    {/* Upcoming / Bookmarks */}
                    <div className="pro-card p-4" data-testid="summary-upcoming">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-amber-500/15 text-amber-700 flex items-center justify-center flex-shrink-0">
                          <CalendarDays className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold text-muted-foreground">
                            You have
                          </div>
                          <div className="font-heading text-xl font-extrabold text-foreground leading-tight">
                            {summary.upcomingCount} upcoming course{summary.upcomingCount === 1 ? '' : 's'}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            From your bookmarks — ready when you are
                          </p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setActiveNav('bookmarks')}
                          className="rounded-full font-bold w-full"
                          data-testid="summary-upcoming-cta"
                        >
                          Browse bookmarks <ArrowUpRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Completed row */}
                  <div className="mt-3 pro-card p-4" data-testid="summary-completed">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl bg-emerald-500/15 text-emerald-700 flex items-center justify-center flex-shrink-0">
                        <Award className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-muted-foreground">
                          You have completed
                        </div>
                        <div className="font-heading text-xl font-extrabold text-foreground leading-tight">
                          {summary.completedCount} course{summary.completedCount === 1 ? '' : 's'}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Review your certified curriculum any time.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate('/my-certificates')}
                        className="rounded-full font-bold flex-shrink-0"
                        data-testid="summary-completed-cta"
                      >
                        Certificates <ArrowUpRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                </section>
              </>
            )}

            {/* SPECIAL EVENTS — upcoming events (no paywall for browsing) */}
            {activeNav === 'special' && (
              <section data-testid="special-events-view">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="eyebrow text-muted-foreground">Upcoming special events</h2>
                  <span className="text-[11px] font-bold text-muted-foreground">{events.length} scheduled</span>
                </div>
                {events.length === 0 ? (
                  <div className="pro-card p-8 text-center">
                    <Sparkles className="w-10 h-10 mx-auto mb-3 text-amber-600" />
                    <h3 className="font-heading text-lg font-extrabold text-foreground">No events scheduled yet</h3>
                    <p className="text-sm text-muted-foreground mt-1">Check back soon — new events land monthly.</p>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {events.map((ev) => (
                      <div key={ev.id || ev.slug || ev.title} className="pro-card p-4 flex flex-col gap-2" data-testid={`special-event-${ev.id || ev.slug}`}>
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-xl bg-amber-500/15 text-amber-700 flex items-center justify-center flex-shrink-0">
                            <CalendarDays className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-heading text-base font-extrabold text-foreground truncate">{ev.title}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {ev.date_label || ev.starts_at || ''}{ev.location ? ` · ${ev.location}` : ''}
                            </div>
                          </div>
                        </div>
                        {ev.description && <p className="text-xs text-muted-foreground line-clamp-2">{ev.description}</p>}
                        <div className="flex items-center justify-between mt-1">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border
                            ${ev.is_ticketed ? 'bg-primary/15 text-primary border-primary/30' : 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30'}`}>
                            {ev.is_ticketed ? `$${((ev.price_cents || 0) / 100).toFixed(0)}` : 'Free RSVP'}
                          </span>
                          <Button size="sm" onClick={() => navigate(ev.is_ticketed ? '/shop' : '/events')} className="rounded-full font-bold bg-primary hover:bg-primary/90">
                            {ev.is_ticketed ? 'Purchase seat' : 'RSVP'} <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* CERTIFICATIONS INLINE */}
            {activeNav === 'certs' && (
              <section data-testid="certs-inline">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="eyebrow text-muted-foreground">Your certifications</h2>
                  <button type="button" onClick={() => navigate('/my-certificates')} className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1">
                    Full page view <ArrowUpRight className="w-3 h-3" />
                  </button>
                </div>
                {certificates.length === 0 ? (
                  <div className="pro-card p-8 text-center">
                    <Award className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <h3 className="font-heading text-lg font-extrabold text-foreground">Trophy shelf is empty</h3>
                    <p className="text-sm text-muted-foreground mt-1">Complete any course to earn your first certificate.</p>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {certificates.map((c) => (
                      <button key={c.cert_number} onClick={() => navigate(`/certificate/${c.cert_number}`)} className="pro-card p-4 text-left hover:border-primary/60 transition-colors">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
                          {c.kind === 'designation' ? 'Professional Designation' : c.kind === 'specialty' ? 'Specialty Certificate' : 'Certificate of Completion'}
                        </div>
                        <div className="font-heading text-base font-extrabold text-foreground truncate mt-1">
                          {c.designation_title || c.specialty_title || c.course_title}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1 font-mono">{c.cert_number}</div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ROADMAP INLINE */}
            {activeNav === 'roadmap' && (
              <section data-testid="roadmap-inline">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="eyebrow text-muted-foreground">Learning roadmap</h2>
                  <button type="button" onClick={() => navigate('/dashboard')} className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1">
                    Open dashboard <ArrowUpRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="pro-card p-6">
                  <div className="grid grid-cols-4 gap-3 text-center">
                    {[
                      { tier: 'Free', pct: courses.filter((c) => c.tier === 'free' && c.progress_percentage === 100).length, total: courses.filter((c) => c.tier === 'free').length, color: 'bg-emerald-500' },
                      { tier: 'Pro', pct: courses.filter((c) => c.tier === 'pro' && c.progress_percentage === 100).length, total: courses.filter((c) => c.tier === 'pro').length, color: 'bg-amber-500' },
                      { tier: 'Certificate', pct: courses.filter((c) => c.tier === 'paid' && c.progress_percentage === 100).length, total: courses.filter((c) => c.tier === 'paid').length, color: 'bg-primary' },
                      { tier: 'Ambassador', pct: certificates.length >= 3 ? 1 : 0, total: 1, color: 'bg-rose-500' },
                    ].map((t) => (
                      <div key={t.tier}>
                        <div className={`h-2 rounded-full ${t.color} mb-2`} style={{ opacity: t.total > 0 ? Math.max(0.25, t.pct / Math.max(1, t.total)) : 0.15 }} />
                        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{t.tier}</div>
                        <div className="font-heading text-lg font-extrabold text-foreground">{t.pct}/{t.total}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* GRADUATE WALL INLINE */}
            {activeNav === 'graduates' && (
              <section data-testid="graduates-inline">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="eyebrow text-muted-foreground">Latest graduates</h2>
                  <button type="button" onClick={() => navigate('/graduates')} className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1">
                    Full wall <ArrowUpRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {graduates.map((g) => (
                    <button key={g.cert_number} onClick={() => navigate(`/certificate/${g.cert_number}`)} className="pro-card p-0 overflow-hidden text-left hover:border-primary/60 transition-colors">
                      <div className="aspect-[1200/630] bg-navy-spotlight">
                        <img src={`${API}/verify/${g.cert_number}/share-card.png`} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="p-3">
                        <div className="font-bold text-sm text-foreground truncate">{g.first_name}{g.last_initial ? ` ${g.last_initial}` : ''}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{g.title}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* STUDY GROUPS */}
            {activeNav === 'studygroups' && (
              <StudyGroupsSection API={API} navigate={navigate} />
            )}

            {/* TIER FILTER TABS — visible on Home / Courses / Enrolled / Bookmarks */}
            {['home', 'courses', 'enrolled', 'bookmarks'].includes(activeNav) && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" role="tablist" aria-label="Filter by tier" data-testid="tier-filter">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex-shrink-0 pr-1">Tier</span>
                  {TIER_FILTERS.map((t) => {
                    const active = tierFilter === t.id;
                    return (
                      <button
                        key={t.id}
                        role="tab"
                        aria-selected={active}
                        type="button"
                        onClick={() => setTierFilter(t.id)}
                        data-testid={`tier-filter-${t.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3.5 text-xs font-bold transition-colors flex-shrink-0
                          ${active ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-card border border-border text-foreground/75 hover:text-foreground hover:border-primary/60'}`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
                {/* STATUS FILTER — Active vs Archived. Only applies to lists that
                    can contain enrolled courses (not Bookmarks). */}
                {['home', 'courses', 'enrolled'].includes(activeNav) && (
                  <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" role="tablist" aria-label="Filter by status" data-testid="status-filter">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex-shrink-0 pr-1">Status</span>
                    {STATUS_FILTERS.map((s) => {
                      const active = statusFilter === s.id;
                      const Icon = s.id === 'archived' ? Archive : s.id === 'active' ? PlayCircle : ListChecks;
                      const count = s.id === 'any'
                        ? courses.filter((c) => c.is_enrolled || (c.completed_lessons || 0) > 0).length
                        : s.id === 'archived'
                        ? courses.filter((c) => c.is_archived).length
                        : courses.filter((c) => (c.is_enrolled || (c.completed_lessons || 0) > 0) && !c.is_archived).length;
                      return (
                        <button
                          key={s.id}
                          role="tab"
                          aria-selected={active}
                          type="button"
                          onClick={() => setStatusFilter(s.id)}
                          data-testid={`status-filter-${s.id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3.5 text-xs font-bold transition-colors flex-shrink-0
                            ${active ? 'bg-foreground text-background shadow-sm' : 'bg-card border border-border text-foreground/75 hover:text-foreground hover:border-foreground/60'}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {s.label}
                          <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? 'bg-background/20' : 'bg-muted text-foreground/70'}`}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* THEME FILTER — multi-select chips derived from course
                categories + skill_tags. Empty selection means "show all". */}
            {['home', 'courses', 'enrolled'].includes(activeNav) && availableThemes.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="theme-filter">
                <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground pr-1">Themes</span>
                {availableThemes.map((t) => {
                  const key = t.toLowerCase();
                  const active = activeThemes.some((x) => x.toLowerCase() === key);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setActiveThemes((prev) =>
                        active
                          ? prev.filter((x) => x.toLowerCase() !== key)
                          : [...prev, t],
                      )}
                      data-testid={`theme-chip-${key.replace(/\s+/g, '-')}`}
                      className={`inline-flex items-center gap-1 rounded-full h-7 px-3 text-[11px] font-bold transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-card border border-border text-foreground/75 hover:text-foreground hover:border-primary/60'
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
                {activeThemes.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveThemes([])}
                    className="text-[11px] font-semibold text-muted-foreground hover:text-foreground underline underline-offset-2"
                    data-testid="theme-clear"
                  >
                    Clear ({activeThemes.length})
                  </button>
                )}
              </div>
            )}

            {/* CURRENT / BROWSE COURSES GRID */}
            {['home', 'courses', 'enrolled', 'bookmarks'].includes(activeNav) && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="eyebrow text-muted-foreground">
                    {activeNav === 'home' ? 'Current courses'
                      : activeNav === 'enrolled' ? 'Your enrolled courses'
                      : activeNav === 'bookmarks' ? 'Bookmarked courses'
                      : 'Courses'}
                  </h2>
                  <span className="text-[11px] font-bold text-muted-foreground">
                    {filteredCourses.length} shown
                  </span>
                </div>

                {/* Active / Archived sub-tabs — visible only under "Your courses" */}
                {activeNav === 'enrolled' && (
                  <div className="mb-3 inline-flex items-center gap-1 rounded-full border border-border bg-card p-1" role="tablist" aria-label="Active or archived courses" data-testid="enrolled-subtabs">
                    {[
                      { id: 'active',   label: 'Active',   icon: PlayCircle,
                        count: courses.filter((c) => (c.is_enrolled || (c.completed_lessons || 0) > 0) && !c.is_archived).length },
                      { id: 'archived', label: 'Archived', icon: Archive,
                        count: courses.filter((c) => (c.is_enrolled || (c.completed_lessons || 0) > 0) && c.is_archived).length },
                    ].map((t) => {
                      const active = statusFilter === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setStatusFilter(t.id)}
                          data-testid={`enrolled-subtab-${t.id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-8 text-xs font-bold transition-colors
                            ${active ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:text-foreground'}`}
                        >
                          <t.icon className="w-3.5 h-3.5" />
                          {t.label}
                          <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? 'bg-background/20' : 'bg-muted text-foreground/70'}`}>{t.count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {loading ? (
                  <div className="py-16 text-center text-muted-foreground" data-testid="learning-loading">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
                    <p className="text-sm">Loading courses…</p>
                  </div>
                ) : filteredCourses.length === 0 ? (
                  <div className="pro-card p-8 text-center" data-testid="learning-empty">
                    <BookOpen className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <h3 className="font-heading text-lg font-extrabold text-foreground">
                      {activeNav === 'bookmarks' ? 'No bookmarks yet'
                        : (activeNav === 'enrolled' && statusFilter === 'archived') ? 'No archived courses'
                        : (activeNav === 'enrolled') ? 'No active courses yet'
                        : 'Nothing here yet'}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
                      {activeNav === 'bookmarks'
                        ? 'Bookmark a course to save it for later. It will appear here for one-tap access.'
                        : (activeNav === 'enrolled' && statusFilter === 'archived')
                        ? 'Archived courses stay tracked for progress and certificates. Archive a course from its card to declutter your active list.'
                        : (activeNav === 'enrolled')
                        ? 'Enroll in a course from the catalog to see it here. Your progress and streak keep building across every course.'
                        : 'New courses land in the catalog every month. Check the Roadmap for what\'s next.'}
                    </p>
                    <Button
                      onClick={() => setActiveNav('home')}
                      className="mt-4 rounded-full font-bold bg-primary hover:bg-primary/90"
                    >
                      Browse all courses
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2" data-testid="learning-course-grid">
                    {filteredCourses.map((c, i) => (
                      <React.Fragment key={c.id}>
                        <CourseCard
                          course={c}
                          onBookmark={toggleBookmark}
                          onOpen={openCourse}
                          onEnroll={enrollCourse}
                          onReminder={setReminderCourse}
                          onArchive={toggleArchive}
                          onDrop={dropCourse}
                          isProUser={isProUser}
                          hasReminder={reminders.some((r) => r.course_id === c.id && r.enabled)}
                          prereqLock={prereqLockMap[c.id]}
                        />
                        {/* Inline ad after the 4th card so it never pushes hero courses down */}
                        {i === 3 && (
                          <div className="sm:col-span-2">
                            <AdSlot location="learning_between" variant="inline" />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </section>
            )}
          </main>

          {/* -------- RIGHT RAIL ---------- */}
          <aside className="hidden lg:block" data-testid="learning-right-rail">
            <div className="sticky top-20 space-y-4">
              {/* Promo card — celebrates the graduate roadmap */}
              <div className="rounded-2xl overflow-hidden bg-navy-spotlight text-background relative p-5" data-testid="learning-promo">
                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-500/25 blur-3xl" />
                <span className="relative inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                  <GraduationCap className="w-3 h-3" /> Your roadmap
                </span>
                <h3 className="relative mt-3 font-heading text-lg font-extrabold leading-snug">
                  {user?.first_name ? `Nice work, ${user.first_name}.` : 'Welcome to the Academy.'}
                </h3>
                <p className="relative text-xs text-background/75 mt-1">
                  Keep the streak alive — every completed lesson unlocks new roadmap tiers.
                </p>
                <div className="relative mt-3 space-y-2">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="w-full text-left rounded-lg bg-background/5 border border-background/15 hover:bg-background/15 transition-colors px-3 py-2"
                    data-testid="learning-promo-roadmap"
                  >
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Comprehensive roadmap</div>
                    <div className="text-sm font-bold">See your progress tiers</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/graduates')}
                    className="w-full text-left rounded-lg bg-background/5 border border-background/15 hover:bg-background/15 transition-colors px-3 py-2"
                    data-testid="learning-promo-wall"
                  >
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Community proof</div>
                    <div className="text-sm font-bold">Visit the Graduate Wall</div>
                  </button>
                </div>
                <div className="relative mt-3 text-right">
                  <button
                    type="button"
                    onClick={() => setActiveNav('enrolled')}
                    className="text-[11px] font-bold text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
                    data-testid="learning-promo-view"
                  >
                    View course list <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Recent lessons */}
              <div className="pro-card p-4" data-testid="learning-recent">
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground mb-2">Recent activity</div>
                {recentProgress.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Complete your first lesson and your recent activity will appear here.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {recentProgress.slice(0, 4).map((p, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <div className={`mt-0.5 h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0
                          ${p.completed ? 'bg-emerald-500/15 text-emerald-600' : 'bg-primary/10 text-primary'}`}>
                          {p.completed ? <CheckCircle2 className="w-3 h-3" /> : <PlayCircle className="w-3 h-3" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-foreground truncate">
                            {p.lesson_title || p.title || 'Lesson'}
                          </div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {p.completed ? 'Completed' : `${p.progress || 0}%`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Study streak counter */}
              <div className="pro-card p-4" data-testid="learning-streak">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground">Study streak</div>
                  <span className="text-[10px] font-mono text-muted-foreground">{streak.days_active} days total</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-4xl font-extrabold text-primary tabular-nums">
                    {streak.current}
                  </span>
                  <span className="text-xs font-semibold text-foreground/80">
                    day{streak.current === 1 ? '' : 's'} in a row
                  </span>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  {streak.current === 0
                    ? 'Complete a lesson today to start your streak.'
                    : streak.current >= 7
                    ? `Longest: ${streak.longest} days. Keep going!`
                    : `Streak in progress. Longest: ${streak.longest} days.`}
                </div>
                {/* Milestone hint */}
                {streak.current > 0 && streak.current < 7 && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-amber-700">
                    {7 - streak.current} more days → 🔥 Week badge
                  </div>
                )}
              </div>

              {/* Calendar — mini month grid + agenda list */}
              <MiniCalendarWithAgenda events={events} reminders={reminders} courses={courses} />
            </div>
          </aside>
        </div>
      </div>

      {reminderCourse && (
        <ReminderDialog
          course={reminderCourse}
          existing={reminders.find((r) => r.course_id === reminderCourse.id)}
          onCancel={() => setReminderCourse(null)}
          onSave={(payload) => saveReminder(reminderCourse, payload)}
        />
      )}

      {plannerOpen && (
        <PlannerDialog
          API={API}
          courses={courses}
          initialMessage={plannerInitialMessage}
          onEnroll={enrollCourse}
          onClose={() => { setPlannerOpen(false); setPlannerInitialMessage(''); }}
        />
      )}
    </div>
  );
};

// -------------------------------------------------------------------------
// Calendar widget — mini month + agenda list, with legend + interactive dots
// -------------------------------------------------------------------------
const MiniCalendarWithAgenda = ({ events = [], reminders = [], courses = [] }) => {
  const now = new Date();
  const [month, setMonth] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selectedDay, setSelectedDay] = useState(null); // 1..31 within current month
  const first = new Date(month.y, month.m, 1);
  const lastDay = new Date(month.y, month.m + 1, 0).getDate();
  const offset = first.getDay();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  // Map day-of-month → array of events landing on it (in the visible month).
  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const raw = e.starts_at || e.date_iso || e.date;
      if (!raw) return;
      const d = new Date(raw);
      if (d.getFullYear() === month.y && d.getMonth() === month.m) {
        const key = d.getDate();
        (map[key] = map[key] || []).push({ ...e, _d: d });
      }
    });
    return map;
  }, [events, month]);

  // Map day-of-month → reminders whose day-of-week matches. (Reminders repeat
  // weekly, so they show on every matching DoW in the month.)
  const remindersByDay = useMemo(() => {
    const enabled = reminders.filter((r) => r.enabled);
    const map = {};
    for (let d = 1; d <= lastDay; d++) {
      const dow = new Date(month.y, month.m, d).getDay();
      const matches = enabled.filter((r) => (r.days_of_week || []).includes(dow));
      if (matches.length) {
        map[d] = matches.map((r) => ({
          ...r,
          course: courses.find((c) => c.id === r.course_id),
        }));
      }
    }
    return map;
  }, [reminders, courses, month, lastDay]);

  const upcoming = useMemo(() => {
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return events
      .map((e) => ({ ...e, _d: (() => { try { return new Date(e.starts_at || e.date_iso || e.date); } catch (_) { return null; } })() }))
      .filter((e) => e._d && e._d >= today0)
      .sort((a, b) => a._d - b._d)
      .slice(0, 3);
  }, [events, now]);

  const monthLabel = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Selected-day agenda — what's actually scheduled on the day the user picked.
  const selectedAgenda = useMemo(() => {
    if (!selectedDay) return null;
    return {
      events: eventsByDay[selectedDay] || [],
      reminders: remindersByDay[selectedDay] || [],
      label: new Date(month.y, month.m, selectedDay).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
      }),
    };
  }, [selectedDay, eventsByDay, remindersByDay, month]);

  return (
    <div className="pro-card p-4" data-testid="learning-calendar">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground">Calendar</div>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous month" onClick={() => { setSelectedDay(null); setMonth(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }); }} className="h-6 w-6 rounded-full text-foreground/60 hover:bg-muted flex items-center justify-center">‹</button>
          <div className="text-xs font-bold text-foreground min-w-[110px] text-center">{monthLabel}</div>
          <button type="button" aria-label="Next month" onClick={() => { setSelectedDay(null); setMonth(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }); }} className="h-6 w-6 rounded-full text-foreground/60 hover:bg-muted flex items-center justify-center">›</button>
        </div>
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {['S','M','T','W','T','F','S'].map((d, i) => (
          <div key={i} className="text-[9px] uppercase font-bold text-muted-foreground/70 py-1">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const today = d === now.getDate() && month.y === now.getFullYear() && month.m === now.getMonth();
          const dayEvents = eventsByDay[d] || [];
          const dayReminders = remindersByDay[d] || [];
          const hasEvent = dayEvents.length > 0;
          const hasReminder = dayReminders.length > 0;
          const isSelected = selectedDay === d;
          const clickable = hasEvent || hasReminder;
          // Rich tooltip so hovering any dot reveals what's planned.
          const tooltip = [
            ...dayEvents.map((e) => `📅 ${e.title}`),
            ...dayReminders.map((r) => `🔔 Study ${r.course?.title || 'course'} @ ${r.time_local}`),
          ].join('\n') || undefined;
          return (
            <button
              key={i}
              type="button"
              title={tooltip}
              onClick={() => clickable && setSelectedDay(isSelected ? null : d)}
              aria-label={clickable
                ? `${d} — ${dayEvents.length} event${dayEvents.length === 1 ? '' : 's'}, ${dayReminders.length} reminder${dayReminders.length === 1 ? '' : 's'}`
                : `${d}`}
              disabled={!clickable}
              data-testid={`calendar-day-${d}`}
              className={`relative py-1.5 rounded-md text-[11px] font-semibold transition-colors
                ${isSelected ? 'ring-2 ring-primary bg-primary/10 text-foreground'
                  : today ? 'bg-primary text-primary-foreground'
                  : clickable ? 'text-foreground/80 hover:bg-muted cursor-pointer'
                  : 'text-foreground/50 cursor-default'}`}
            >
              {d}
              <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                {hasEvent && <span className="h-1 w-1 rounded-full bg-amber-500" aria-hidden="true" />}
                {hasReminder && <span className="h-1 w-1 rounded-full bg-primary" aria-hidden="true" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend — makes the dots self-explanatory */}
      <div className="mt-3 flex items-center gap-3 text-[10px] font-semibold text-muted-foreground" data-testid="calendar-legend">
        <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Event</span>
        <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> Study reminder</span>
        <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-primary ring-2 ring-primary/30" /> Today</span>
      </div>

      {/* Selected-day drill-in */}
      {selectedAgenda && (
        <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-2.5" data-testid="calendar-selected-agenda">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">{selectedAgenda.label}</div>
            <button type="button" onClick={() => setSelectedDay(null)} className="text-[10px] font-bold text-muted-foreground hover:text-foreground" data-testid="calendar-clear-selection">Clear</button>
          </div>
          {selectedAgenda.events.length === 0 && selectedAgenda.reminders.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">Nothing scheduled.</p>
          ) : (
            <div className="space-y-1.5">
              {selectedAgenda.events.map((e, i) => (
                <div key={`e-${i}`} className="flex items-start gap-2 text-xs">
                  <div className="mt-0.5 h-4 w-4 rounded-sm bg-amber-500/20 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-2.5 h-2.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-foreground truncate">{e.title}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {e.time_label || e._d?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) || 'All day'}
                      {e.location ? ` · ${e.location}` : ''}
                    </div>
                  </div>
                </div>
              ))}
              {selectedAgenda.reminders.map((r, i) => (
                <div key={`r-${i}`} className="flex items-start gap-2 text-xs">
                  <div className="mt-0.5 h-4 w-4 rounded-sm bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                    <Bell className="w-2.5 h-2.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-foreground truncate">
                      Study {r.course?.title || 'course'}
                    </div>
                    <div className="text-[10px] text-muted-foreground">Reminder · {r.time_local}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Up next — always visible so the user sees what's coming even before
          they click into a specific day. */}
      <div className="mt-3 space-y-2">
        <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">Up next</div>
        {upcoming.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No scheduled events. Add reminders on any enrolled course to see them here.</p>
        ) : (
          upcoming.map((e, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <div className="mt-0.5 h-5 w-5 rounded-md bg-amber-500/15 text-amber-700 flex items-center justify-center flex-shrink-0">
                <CalendarDays className="w-3 h-3" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-foreground truncate">{e.title}</div>
                <div className="text-[10px] text-muted-foreground">
                  {e._d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  {e.time_label ? ` · ${e.time_label}` : ''}
                </div>
              </div>
            </div>
          ))
        )}
        {reminders.filter((r) => r.enabled).slice(0, 2).map((r) => {
          const c = courses.find((c) => c.id === r.course_id);
          if (!c) return null;
          const dowNames = (r.days_of_week || []).map((d) => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]).join(', ');
          return (
            <div key={r.course_id} className="flex items-start gap-2 text-xs">
              <div className="mt-0.5 h-5 w-5 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Bell className="w-3 h-3" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-foreground truncate">{c.title}</div>
                <div className="text-[10px] text-muted-foreground">{dowNames || 'Daily'} · {r.time_local}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
// Reminder dialog
// -------------------------------------------------------------------------
const ReminderDialog = ({ course, existing, onCancel, onSave }) => {
  const [days, setDays] = useState(existing?.days_of_week || [1, 3, 5]);
  const [time, setTime] = useState(existing?.time_local || '09:00');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const dowLabels = ['S','M','T','W','T','F','S'];
  const toggleDay = (i) => setDays((prev) => prev.includes(i) ? prev.filter((d) => d !== i) : [...prev, i].sort());
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/70 backdrop-blur" data-testid="reminder-dialog" onClick={onCancel}>
      <div className="pro-card p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-4 h-4 text-primary" />
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Study reminder</div>
        </div>
        <h3 className="font-heading text-lg font-extrabold text-foreground leading-tight">{course.title}</h3>
        <p className="text-xs text-muted-foreground mt-1">Pick the days + time to get a push notification.</p>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Days</div>
          <div className="flex items-center gap-1">
            {dowLabels.map((d, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleDay(i)}
                data-testid={`reminder-day-${i}`}
                className={`h-9 w-9 rounded-full text-xs font-bold transition-colors
                  ${days.includes(i) ? 'bg-primary text-primary-foreground' : 'border border-border text-foreground/70 hover:bg-muted'}`}
              >{d}</button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Time</div>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-border bg-card text-sm font-semibold"
            data-testid="reminder-time"
          />
        </div>
        <label className="mt-4 flex items-center gap-2 text-xs font-semibold text-foreground/80">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="reminder-enabled" />
          Reminder enabled
        </label>
        <div className="mt-5 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onCancel} className="rounded-full font-bold" data-testid="reminder-cancel">Cancel</Button>
          <Button onClick={() => onSave({ days_of_week: days, time_local: time, enabled })} className="rounded-full font-bold bg-primary hover:bg-primary/90" data-testid="reminder-save">Save reminder</Button>
        </div>
      </div>
    </div>
  );
};

export default LearningCatalog;

// -------------------------------------------------------------------------
// Prof. FreedrIQ — chat modal + optional quiz
// -------------------------------------------------------------------------
const QUIZ_STEPS = [
  { key: 'experience', label: "What's your experience?", options: ['Just starting', 'Some batches done', 'Small business', 'Selling professionally'] },
  { key: 'goal',       label: "What's your main goal?", options: ['Home preservation', 'Farmers market side hustle', 'Full-time freeze-dry business', 'Enterprise / co-packer'] },
  { key: 'hours',      label: "Hours per week to study?", options: ['<1h', '1–3h', '3–5h', '5h+'] },
  { key: 'focus',      label: "Focus area?", options: ['Foods & recipes', 'Machine mechanics', 'Business & sales', 'Advanced science'] },
];

const PlannerDialog = ({ API, courses, onEnroll, onClose, initialMessage }) => {
  const [phase, setPhase] = useState(initialMessage ? 'chat' : 'quiz');  // 'quiz' → 'chat'
  const [quizIdx, setQuizIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`planner-${Date.now()}`);

  // Auto-send the confidence-survey handoff prompt on first mount so the
  // struggling user lands in an active conversation. Runs once per instance.
  const initialSentRef = useRef(false);
  useEffect(() => {
    if (initialMessage && !initialSentRef.current) {
      initialSentRef.current = true;
      sendMessage(initialMessage, {});
    }
  }, [initialMessage]);

  const finishQuiz = async (finalAnswers) => {
    setPhase('chat');
    const goalPrompt = `Based on my profile, recommend courses to start with.`;
    await sendMessage(goalPrompt, finalAnswers);
  };

  const sendMessage = async (text, ctx) => {
    if (!text.trim()) return;
    setLoading(true);
    setMessages((m) => [...m, { role: 'user', text }]);
    try {
      const { data } = await axios.post(`${API}/lms/planner/chat`, {
        message: text,
        session_id: sessionId,
        context: ctx || answers,
      });
      const recCourses = (data.recommendations || [])
        .map((id) => courses.find((c) => c.id === id))
        .filter(Boolean);
      setMessages((m) => [...m, { role: 'assistant', text: data.reply || '', recommendations: recCourses }]);
    } catch (_) {
      setMessages((m) => [...m, { role: 'assistant', text: "Sorry, I couldn't reach the planner right now.", recommendations: [] }]);
    } finally {
      setLoading(false);
    }
    setInput('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur" data-testid="planner-dialog" onClick={onClose}>
      <div className="pro-card w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-navy-spotlight text-background p-5 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-amber-500/25 blur-3xl" />
          <div className="relative flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-500 text-navy-950 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Prof. FreedrIQ</div>
              <div className="font-heading text-lg font-extrabold leading-tight">Let&apos;s build your learning path</div>
              <p className="text-[11px] text-background/70 mt-0.5">Answer a few questions or just describe your goal.</p>
            </div>
            <button type="button" onClick={onClose} className="text-background/70 hover:text-background text-xs font-bold" data-testid="planner-close">Close ✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {phase === 'quiz' && (
            <div data-testid="planner-quiz">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">
                Step {quizIdx + 1} of {QUIZ_STEPS.length}
              </div>
              <div className="font-heading text-lg font-extrabold text-foreground">{QUIZ_STEPS[quizIdx].label}</div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {QUIZ_STEPS[quizIdx].options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      const next = { ...answers, [QUIZ_STEPS[quizIdx].key]: opt };
                      setAnswers(next);
                      if (quizIdx + 1 < QUIZ_STEPS.length) setQuizIdx(quizIdx + 1);
                      else finishQuiz(next);
                    }}
                    data-testid={`planner-quiz-${QUIZ_STEPS[quizIdx].key}-${opt.slice(0, 6)}`}
                    className="text-left rounded-xl border border-border p-3 hover:border-primary/60 hover:bg-primary/5 transition-colors"
                  >
                    <div className="text-sm font-bold text-foreground">{opt}</div>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => { setPhase('chat'); }}
                className="mt-4 text-xs font-bold text-primary hover:underline"
                data-testid="planner-skip-quiz"
              >
                Skip quiz — just chat →
              </button>
            </div>
          )}

          {phase === 'chat' && (
            <>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm
                    ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    {m.recommendations && m.recommendations.length > 0 && (
                      <div className="mt-3 space-y-2" data-testid="planner-recommendations">
                        {m.recommendations.map((c) => (
                          <div key={c.id} className="rounded-lg bg-background border border-border p-2 flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-xs font-bold text-foreground truncate">{c.title}</div>
                              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                                {c.tier} · {c.total_lessons || 0} lessons
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => onEnroll(c)}
                              className="rounded-full font-bold bg-primary hover:bg-primary/90 flex-shrink-0"
                              data-testid={`planner-enroll-${c.id}`}
                            >Enroll</Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> The Planner is thinking…
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {phase === 'chat' && (
          <div className="border-t border-border p-3 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !loading) sendMessage(input); }}
              placeholder="Ask a follow-up… e.g. 'What if I only have 2 hours a week?'"
              className="flex-1 h-10 px-3 rounded-full border border-border bg-card text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
              data-testid="planner-input"
              disabled={loading}
            />
            <Button
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim()}
              className="rounded-full font-bold bg-primary hover:bg-primary/90"
              data-testid="planner-send"
            >Send</Button>
          </div>
        )}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// StudyGroupsSection — pinned rail on the Learning Board that lists upcoming
// + past study rooms and lets any learner spin up a new one on the spot.
// ---------------------------------------------------------------------------
const StudyGroupsSection = ({ API, navigate }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', when: 'now', scheduled: '', duration: 60, max_seats: 8,
  });

  const load = React.useCallback(() => {
    setLoading(true);
    axios.get(`${API}/live-sessions?session_type=study_room&limit=30`)
      .then((r) => setRooms(Array.isArray(r.data) ? r.data : (r.data?.sessions || [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!createOpen) return;
    setForm((f) => ({
      ...f,
      scheduled: f.scheduled || new Date(Date.now() + 15 * 60 * 1000).toISOString().slice(0, 16),
    }));
  }, [createOpen]);

  const createGroup = async () => {
    if (creating || !form.title.trim()) return;
    setCreating(true);
    try {
      const scheduledIso = form.when === 'now'
        ? new Date().toISOString()
        : new Date(form.scheduled).toISOString();
      const { data } = await axios.post(`${API}/live-sessions`, {
        title:            form.title.slice(0, 80),
        description:      form.description || '',
        scheduled_start:  scheduledIso,
        duration_minutes: Math.max(15, Math.min(240, parseInt(form.duration, 10) || 60)),
        max_attendees:    Math.max(2, Math.min(50, parseInt(form.max_seats, 10) || 8)),
        session_type:     'study_room',
        features:         { whiteboard: true, breakouts: true, chat: true, screenshare: true },
      });
      toast.success(form.when === 'now' ? 'Study group live!' : 'Study group scheduled');
      setCreateOpen(false);
      if (form.when === 'now') navigate(`/live/${data.id}/room`);
      else navigate(`/live/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create study group');
    } finally { setCreating(false); }
  };

  const now = Date.now();
  const upcoming = rooms.filter((r) => new Date(r.scheduled_start || 0).getTime() >= now - 30 * 60 * 1000);
  const past     = rooms.filter((r) => new Date(r.scheduled_start || 0).getTime() < now - 30 * 60 * 1000);

  return (
    <section data-testid="studygroups-inline" className="space-y-4">
      <div className="pro-card p-5 relative overflow-hidden border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Study Groups
            </div>
            <h2 className="font-heading text-2xl font-extrabold text-foreground mt-1">
              Learn together, live
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed max-w-lg">
              Spin up a peer-hosted room with whiteboard, breakout rooms and chat.
              Every session is auto-recorded, transcribed and chapterised — so no
              one who missed live has to catch up alone.
            </p>
          </div>
          <Presentation className="hidden sm:block w-16 h-16 text-primary/25 flex-shrink-0" strokeWidth={1.5} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            onClick={() => setCreateOpen(true)}
            className="rounded-full font-bold"
            data-testid="studygroups-create-btn"
          >
            <Video className="w-3.5 h-3.5 mr-1.5" /> Start a study group
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/study-rooms')}
            className="rounded-full font-bold"
            data-testid="studygroups-hub-btn"
          >
            Full Study Rooms hub <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>

      <div className="pro-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-extrabold text-sm">Upcoming</h3>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
        </div>
        {upcoming.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-2">
            No upcoming study groups. Be the first to schedule one!
          </div>
        ) : (
          <ul className="space-y-2">
            {upcoming.slice(0, 6).map((r) => (
              <li
                key={r.id}
                className="rounded-lg border border-border p-3 flex items-center gap-3 hover:border-primary/50 transition-colors"
                data-testid={`upcoming-room-${r.id}`}
              >
                <Video className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{r.title || 'Study room'}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.scheduled_start ? new Date(r.scheduled_start).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    {r.attendee_count ? ` · ${r.attendee_count} joined` : ''}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/live/${r.id}`)} className="rounded-full h-7 text-[11px]">Details</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {past.length > 0 && (
        <div className="pro-card p-4">
          <h3 className="font-heading font-extrabold text-sm mb-3">Past sessions</h3>
          <ul className="space-y-2">
            {past.slice(0, 6).map((r) => (
              <li key={r.id} className="rounded-lg border border-border p-3 flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{r.title || 'Study room'}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {r.scheduled_start ? new Date(r.scheduled_start).toLocaleString(undefined, { month: 'short', day: 'numeric' }) : ''}
                    {r.attendee_count ? ` · ${r.attendee_count} joined` : ''}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/live/${r.id}`)} className="rounded-full h-7 text-[11px]">Recording</Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {createOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setCreateOpen(false); }}
          data-testid="studygroups-create-dialog"
        >
          <div className="w-full max-w-lg rounded-2xl bg-card p-6 shadow-2xl space-y-3">
            <div>
              <div className="font-heading text-xl font-extrabold inline-flex items-center gap-2">
                <Presentation className="w-5 h-5 text-primary" /> Start a study group
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Whiteboard, breakout rooms and auto-transcription come standard.
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Group name *</label>
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Batch prep — Wednesday cohort"
                maxLength={80}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                data-testid="studygroups-form-title"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">What will you work on? · optional</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="Troubleshooting collapse issues, sharing pricing spreadsheets…"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1 resize-none"
                data-testid="studygroups-form-desc"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">When</label>
                <div className="flex rounded-full border border-input p-0.5 mt-1">
                  {['now', 'later'].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, when: v }))}
                      data-testid={`studygroups-form-when-${v}`}
                      className={`flex-1 h-8 rounded-full text-xs font-bold transition-colors ${
                        form.when === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {v === 'now' ? 'Start now' : 'Schedule'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Max seats</label>
                <input
                  type="number"
                  min="2"
                  max="50"
                  value={form.max_seats}
                  onChange={(e) => setForm((f) => ({ ...f, max_seats: e.target.value }))}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                  data-testid="studygroups-form-max-seats"
                />
              </div>
            </div>
            {form.when === 'later' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Start time</label>
                  <input
                    type="datetime-local"
                    value={form.scheduled}
                    onChange={(e) => setForm((f) => ({ ...f, scheduled: e.target.value }))}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                    data-testid="studygroups-form-scheduled"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Duration (min)</label>
                  <input
                    type="number"
                    min="15"
                    max="240"
                    value={form.duration}
                    onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating} className="rounded-full font-bold">Cancel</Button>
              <Button
                onClick={createGroup}
                disabled={creating || !form.title.trim() || (form.when === 'later' && !form.scheduled)}
                className="rounded-full font-bold"
                data-testid="studygroups-form-submit"
              >
                {creating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> creating…</> : form.when === 'now' ? 'Start now' : 'Schedule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
